import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import { Alert, Box, FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { UserDetailsContext } from '../../../../../context/user-details/user-details';
import CapitalGoodService from '../../../../../shared/services/capital-good/capital-good-service';
import FinishedProductEntryService from '../../../../../shared/services/finished-product-entry/finishedProductEntry.service';
import { ReportPeriodContext } from '../../../../../context/report-periods/report-periods';
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications';
import { ScreenLabelsContext } from '../../../../../context/screen-labels/screen-labels';
import { ICapitalGood } from '../../../../../shared/services/capital-good/capital-good.model';
import { ShallowFormDisplay } from '../../../form-field-components/general/shallow-form-display';
import CustomFieldService from '../../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../../shared/services/custom-field/custom-field.model';
import { EmissionsInputAdvanced } from './emissions-input-advanced-form';
import { DialogSaveButtons } from '../../../miscellaneous-components/dialog-save-buttons';
import { ISubsidiary } from '../../../../../shared/services/subsidiary/subsidiary.model';
import { formatTransaction } from '../../../../../shared/utils/format-post-utils';
import { useIsReportPeriodLocked } from '../../../../../hooks/use-is-report-period-locked';
import { getPreferredSubsidiary, getSelectableSubsidiaries } from '../../../../../shared/utils/subsidiary-utils';
import { IEmission } from '../../../../../shared/services/emission/emission.model';
import EmissionCalculationService, { ISupersedeDecision, SupersedeResolver } from '../../../../../shared/services/emission-calculation/emission-calculation.service';
import { IEmissionCalculation } from '../../../../../shared/services/emission-calculation/emission-calculation.model';
import { resolveEmissionFieldFlags } from '../../../../../shared/utils/emission-field-config';
import { SOLD_PRODUCT_STAGE_BY_GROUP_ID } from '../../../../../shared/constants/group-ids';

export const EmissionsInputGoods: FunctionComponent<{
  selectedGoodId: string,
  subsidiaries: ISubsidiary[],
  setViewEmissions: React.Dispatch<React.SetStateAction<any>>,
  groupId: string,
  onSave: (emissionCalculationIds?: string[]) => void,
  emission?: IEmission | null,
  editCalculation?: IEmissionCalculation | null,
  editCalculationIds?: string[],
  cancelHandler?: () => void,
  resolveSupersede?: SupersedeResolver,
}> = ({
  selectedGoodId,
  setViewEmissions,
  groupId,
  subsidiaries,
  onSave,
  emission,
  editCalculation,
  editCalculationIds,
  cancelHandler,
  resolveSupersede,
}) => {
    //Services
    const capitalGoodService = new CapitalGoodService();
    const finishedProductEntryService = new FinishedProductEntryService();
    const emissionCalculationService = new EmissionCalculationService();
    const customFieldService = new CustomFieldService()
    const reportPeriodContext = useContext(ReportPeriodContext);
    const userNotificationsContext = useContext(UserNotificationsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const userDetails = userDetailsContext.userDetails
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [capitalGood, setCapitalGood] = useState<ICapitalGood>()
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const [openStartDate, setOpenStartDate] = useState(false);
    const fieldFlags = useMemo(
      () => resolveEmissionFieldFlags(userDetails?.organisation?.emissionFieldConfig, customFields ?? []),
      [userDetails?.organisation?.emissionFieldConfig, customFields],
    );
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //Loading State
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    // Sold products book downstream: units sold × the group's stage figure (stage B lifetime
    // use-phase on Use of Sold Products, stage C on End of Life Treatment). Purchased and
    // capital goods multiply the headline cradle-to-gate factor.
    const isSoldProduct = capitalGood?.typeOfGood === 'soldProduct'
    const stageConfig = isSoldProduct ? SOLD_PRODUCT_STAGE_BY_GROUP_ID[groupId] : undefined
    const perUnitKg = stageConfig ? capitalGood?.[stageConfig.stageField] : capitalGood?.kgCarbonEmission
    const missingStageFigure = isSoldProduct && (perUnitKg == null)

    useEffect(() => {
      capitalGoodService.getOne(selectedGoodId)
        .then(response => {
          setCapitalGood(response)
        })
        .catch(err => console.log(err))
    }, [selectedGoodId])

    useEffect(() => {
      customFieldService.getAllActive(`&entryType=transaction`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => {
          console.log(err)
        })
    }, [])

    const finishedProductSchema = useMemo(() => {
      // Config-driven contextual fields are only collected when creating a new emission.
      const req = (key: string, label: string) =>
        !emission && fieldFlags.mandatoryFields.includes(key)
          ? Yup.string().required(`${label} is required`)
          : Yup.string().nullable();
      const customFieldShape: Record<string, any> = {};
      (customFields ?? []).forEach(cf => {
        customFieldShape[cf.id] = !emission && fieldFlags.mandatoryFields.includes(cf.id)
          ? Yup.string().required(`${cf.fieldName} is required`)
          : Yup.string().nullable();
      });
      return Yup.object().shape({
        subsidiaryId: emission ? Yup.string().nullable() : Yup.string().required(`Please select a ${screenLabelsContext.getLabel('subsidiary', { plural: false })}`),
        unitsConsumed: Yup.number().required('Please enter the amount of units produced'),
        startDate: emission ? Yup.string().nullable() : Yup.string().required('Please enter a date'),
        projectId: req('project', 'Project'),
        locationId: req('location', 'Location'),
        contactSupplierId: req('supplier', 'Supplier'),
        countryId: emission ? Yup.string().nullable() : Yup.string().required('Please select a country'),
        vehicleId: req('vehicle', 'Vehicle'),
        excludeFromReporting: Yup.boolean(),
        excludeFromReportingReason: Yup.string().when('excludeFromReporting', {
          is: true,
          then: (schema) => schema.required('You must provide a reason for exclusion'),
          otherwise: (schema) => schema.nullable().notRequired(),
        }),
        ...customFieldShape,
      });
    }, [emission, screenLabelsContext, fieldFlags, customFields]);

    const formik = useFormik<any>({
      initialValues: {
        subsidiaryId: emission?.subsidiaryId ?? null,
        unitsConsumed: editCalculation?.unitsConsumed ?? '',
        startDate: emission?.startDate ?? '',
        projectId: null,
        locationId: null,
        contactSupplierId: null,
        countryId: emission?.countryId ?? null,
        vehicleId: null,
        excludeFromReporting: emission?.excludeFromReporting ?? false,
        excludeFromReportingReason: emission?.excludeFromReportingReason ?? '',
        isFlag: editCalculation?.isFlag === true,
        flagCategory: editCalculation?.flagCategory ?? '',
      },
      validationSchema: finishedProductSchema,
      onSubmit: async (values) => {
        values.reportPeriodId = reportPeriodContext.lastSelectedReportId;
        values.entryType = 'capitalGood';
        values.endDate = values.startDate;
        values.capitalGoodId = selectedGoodId;
        if (missingStageFigure) return;
        if (isSoldProduct) {
          // Sold products book downstream: the backend cannot derive the group from the good,
          // so the target group (Use of Sold Products) travels in the payload.
          values.groupId = groupId;
        }
        const response = formatTransaction(values, groupId, true, customFields);
        if (isReportPeriodLocked) {
          userNotificationsContext.openDialog({
            active: true,
            title: 'Action Not Allowed',
            message: `The selected report period is locked and cannot be modified`,
            buttonCopy: 'Ok',
            hideCancelButton: true
          })
          return
        }
        if (isSubmitting) return
        setIsSubmitting(true)

        if (emission) {
          const transaction = {
            groupId,
            unitsConsumed: values.unitsConsumed,
            capitalGoodId: selectedGoodId,
            type: 'PRODUCT',
            isFlag: values.isFlag === true,
            flagCategory: values.flagCategory || undefined,
          }
          let savedMessage = 'Saved successfully'
          let supersede = false
          if (!editCalculationIds?.length && resolveSupersede) {
            let decision: ISupersedeDecision
            try {
              decision = await resolveSupersede()
            } catch {
              setIsSubmitting(false)
              return
            }
            supersede = decision.supersede
            savedMessage = decision.savedMessage
          }
          const calculationAction = editCalculationIds?.length
            ? emissionCalculationService.editEmissionCalculation(editCalculationIds, transaction)
            : emissionCalculationService.createEmissionCalculation(emission.id, transaction, supersede)
          calculationAction
            .then((response) => {
              userNotificationsContext.openSnackbar({ open: true, message: savedMessage });
              setIsSubmitting(false)
              onSave('emissionCalculationIds' in response ? response.emissionCalculationIds : undefined)
            })
            .catch(err => {
              if (err.response?.data?.code === 1) {
                userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              }
              console.log(err)
              setIsSubmitting(false)
            })
        } else {
          finishedProductEntryService.create(response)
            .then(() => {
              userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
              setViewEmissions('groupTable')
              setIsSubmitting(false)
              onSave()
            })
            .catch(err => {
              if (err.response?.data?.code === 1) {
                userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
              } else {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              }
              console.log(err)
              setIsSubmitting(false)
            })
        }
      }
    })

    //Use Effects
    useEffect(() => {
      if (userDetails && subsidiaries) {
        const preferred = getPreferredSubsidiary(subsidiaries, userDetails)
        if (preferred) {
          formik.setFieldValue('subsidiaryId', preferred.id)
          formik.setFieldValue('countryId', preferred.countryId)
        }
      }
    }, [userDetails, subsidiaries])

    //Formik Event Handlers
    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setValues({ ...formik.values, [name]: date.format('YYYY-MM-DD') })
      }
    }

    const subsidiaryHandler = (e: SelectChangeEvent<string>) => {
      const subsidiary = subsidiaries.find(s => s.id === e.target.value)
      if (subsidiary) {
        formik.setFieldValue('subsidiaryId', subsidiary.id)
        formik.setFieldValue('countryId', subsidiary.countryId)
      }
    }

    // Live preview mirrors the backend exactly: emissionAmount = perUnitKg × unitsConsumed
    // (unitAmount is descriptive only and is NOT a multiplier, so it is not included here).
    const estimatedTotalKg = (perUnitKg || 0) * (Number(formik.values.unitsConsumed) || 0)

    return (
      <Box>
        {capitalGood && <>
          <Typography variant='h6' sx={{ color: 'text.primary', mb: 4, mt: 1 }}> Add Emissions </Typography>
          <Box sx={{ mb: 2 }}>
            <ShallowFormDisplay name='Name' value={capitalGood.name} isTopRow={true} small={true}   ></ShallowFormDisplay>
            <ShallowFormDisplay name='Model Number' value={capitalGood.modelNumber} small={true}></ShallowFormDisplay>
            {capitalGood.description && <ShallowFormDisplay name='Description' value={capitalGood.description} small={true}></ShallowFormDisplay>}
            {capitalGood.manufacturer && <ShallowFormDisplay name='Manufacturer' value={capitalGood.manufacturer.name} small={true}></ShallowFormDisplay>}
            {capitalGood.measurementUnit && <ShallowFormDisplay name='Unit' value={`${capitalGood.unitAmount} ${capitalGood.measurementUnit.name}`} small={true}></ShallowFormDisplay>}
            <ShallowFormDisplay
              name={stageConfig ? stageConfig.perUnitLabel : 'kg CO2e per unit'}
              value={perUnitKg ?? 'N/A'}
              isBottomRow={true} small={true}></ShallowFormDisplay>
          </Box>
          {missingStageFigure && stageConfig &&
            <Alert severity='warning' sx={{ mb: 2 }}>
              This {screenLabelsContext.getLabel('product')} has no {stageConfig.missingFigureText} figure
              recorded, so these emissions cannot be booked yet. Record the stage figures from its LCA or
              EPD on the {screenLabelsContext.getLabel('product')} page first.
            </Alert>
          }
          <Stack direction='column' spacing={2} >
            {!emission && (
              <Stack direction='row' spacing={2}>
                <FormControl fullWidth variant='outlined'>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      minDate={moment(reportPeriodContext.getAllowedInputDates()?.startDate)}
                      maxDate={formik.values.endDate ? moment(formik.values.endDate) : moment(reportPeriodContext.getAllowedInputDates()?.endDate)}
                      label={isSoldProduct ? 'Date sold' : 'Date Purchased'}
                      value={formik.values.startDate ? moment(formik.values.startDate) : null}
                      onChange={(date) => dateHandler(date, 'startDate')}
                      format='DD/MM/YYYY'
                      sx={{ color: 'text.primary' }}
                      open={openStartDate}
                      onOpen={() => setOpenStartDate(true)}
                      onClose={() => setOpenStartDate(false)}
                      slotProps={{
                        textField: {
                          onClick: () => setOpenStartDate(true),
                          error: formik.touched.startDate && Boolean(formik.errors.startDate)
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {formik.touched.startDate && formik.errors.startDate &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.startDate ? <div>{formik.errors.startDate as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>
            </Stack>
            )}
            <FormControl fullWidth variant='outlined'>
              <InputLabel>{isSoldProduct ? 'Units sold in the period' : 'Units Purchased'}</InputLabel>
              <OutlinedInput inputProps={{ min: 0.01, step: 0.01 }} label={isSoldProduct ? 'Units sold in the period' : 'Units Purchased'} name='unitsConsumed' type='number'
                value={formik.values.unitsConsumed || ''} onChange={formik.handleChange}
                error={formik.touched.unitsConsumed && Boolean(formik.errors.unitsConsumed)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.unitsConsumed && formik.errors.unitsConsumed &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.unitsConsumed ? <div>{formik.errors.unitsConsumed as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
            {estimatedTotalKg > 0 &&
              <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'lightGrey.light', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant='body2' color='text.secondary'>{stageConfig ? stageConfig.previewLabel : 'Estimated total'}</Typography>
                <Typography variant='subtitle1'><b>{new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(estimatedTotalKg)} kg CO₂e</b></Typography>
              </Box>
            }
            {!emission && subsidiaries && (userDetails?.organisation?.enableSubsidiaries || subsidiaries.length > 1) &&
              <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
                <InputLabel htmlFor='subsidiaryId'> {screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' })}</InputLabel>
                <Select
                  id='subsidiaryId' label={screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' })} name='subsidiaryId'
                  value={formik.values.subsidiaryId || ''}
                  onChange={subsidiaryHandler}
                  error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                  sx={{ color: 'text.pprimary', }}>
                  {getSelectableSubsidiaries(subsidiaries).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) =>
                    <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
                  )}
                </Select>
                {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.subsidiaryId ? <div>{formik.errors.subsidiaryId as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>
            }
            {!emission && customFields &&
              <EmissionsInputAdvanced
                customFields={fieldFlags.visibleCustomFields}
                formik={formik}
                hideCountry={fieldFlags.hideCountry}
                hideLocation={fieldFlags.hideLocation}
                hideSupplier={fieldFlags.hideSupplier}
                hideVehicle={fieldFlags.hideVehicle}
                hideProject={fieldFlags.hideProject}
                hideExcludedFromReporting={fieldFlags.hideExcludeFromReporting}
                hideRegulatedEmissionTradingScheme={true}
                hideProduct={true}
                hideRec={true}
                hideCustomBasedMarketMultiplier={true}
                mandatoryFields={fieldFlags.mandatoryFields}
              />}
            {!missingStageFigure &&
              <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
                <DialogSaveButtons
                  isForm={true}
                  isSubmitting={isSubmitting}
                  cancelHandler={cancelHandler}
                  hideCancelButton={!cancelHandler}
                  submitHandler={formik.submitForm}
                />
              </Stack>
            }
          </Stack>
        </>}
      </Box>
    )
  }